import { useEffect, useState } from "react";
import { ChannelFilters, StreamChat } from "stream-chat";

interface UseUnreadCountOptions {
  unresolvedOnly?: boolean;
}

export function useUnreadCount(
  client: StreamChat | null,
  options?: UseUnreadCountOptions,
) {
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [unreadChannelsCount, setUnreadChannelsCount] = useState(0);
  const unresolvedOnly = options?.unresolvedOnly ?? false;

  useEffect(() => {
    if (!client) {
      setTotalUnreadCount(0);
      setUnreadChannelsCount(0);
      return;
    }

    let isMounted = true;

    const userId = client.userID;
    if (!userId) {
      setTotalUnreadCount(0);
      setUnreadChannelsCount(0);
      return;
    }

    let inFlight = false;
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;

    const countChannels = async () => {
      if (inFlight) return;
      inFlight = true;

      const filters: ChannelFilters = unresolvedOnly
        ? ({
            type: "messaging",
            members: { $in: [userId] },
            $or: [{ resolved: { $exists: false } }, { resolved: false }],
          } as ChannelFilters)
        : {
            type: "messaging",
            members: { $in: [userId] },
          };

      try {
        const limit = 30;
        let offset = 0;
        let total = 0;
        let unreadChannels = 0;

        while (true) {
          const channels = await client.queryChannels(
            filters,
            {},
            {
              limit,
              offset,
              watch: false,
              state: true,
              message_limit: 0,
            },
          );

          total += channels.length;
          unreadChannels += channels.filter(
            (channel) => channel.countUnread() > 0,
          ).length;

          if (channels.length < limit) {
            break;
          }

          offset += limit;
        }

        if (isMounted) {
          setTotalUnreadCount(total);
          setUnreadChannelsCount(unreadChannels);
        }
      } catch (error) {
        // Unread badges are supplemental UI. Stream rate limits and transient
        // connection failures must not surface as unhandled runtime errors.
        console.warn("Failed to refresh Stream Chat unread counts:", error);
      } finally {
        inFlight = false;
      }
    };

    void countChannels();

    const scheduleRecount = () => {
      if (throttleTimer) return;

      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        void countChannels();
      }, 5000);
    };

    const handlers = [
      client.on("notification.added_to_channel", scheduleRecount),
      client.on("notification.removed_from_channel", scheduleRecount),
      client.on("channel.deleted", scheduleRecount),
      client.on("channel.hidden", scheduleRecount),
      client.on("channel.visible", scheduleRecount),
      client.on("connection.recovered", scheduleRecount),
      client.on("message.new", scheduleRecount),
      client.on("notification.message_new", scheduleRecount),
      client.on("message.read", scheduleRecount),
      client.on("notification.mark_read", scheduleRecount),
      client.on("notification.mark_unread", scheduleRecount),
    ];

    const refreshInterval = setInterval(() => {
      void countChannels();
    }, 60000);

    return () => {
      isMounted = false;

      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }

      clearInterval(refreshInterval);

      handlers.forEach((handler) => handler.unsubscribe());
    };
  }, [client, unresolvedOnly]);

  return { totalUnreadCount, unreadChannelsCount };
}
